// Dashboard sub-panels for AUVRA investor cockpit
import { useEffect, useState, useMemo } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import {
  Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Coins,
  Copy, Download, Upload, Shield, BellRing, CheckCircle2, Clock,
  FileText, Settings as SettingsIcon, User as UserIcon, LifeBuoy,
  Zap, PieChart as PieIcon, ShieldCheck, MailCheck, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const fmt = (n, d = 3) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const cap = (n) => n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${fmt(n, 0)}`;

const BLUE = "#2563EB";

const Card = ({ children, className = "", testid }) => (
  <div className={`glass rounded-2xl p-6 ${className}`} data-testid={testid}>{children}</div>
);

const Stat = ({ label, value, sub, positive, icon: Icon, testid }) => (
  <Card testid={testid}>
    <div className="flex items-center gap-2 text-secondary-auvra text-xs uppercase tracking-wider mb-2">
      {Icon && <Icon className="w-4 h-4" />}{label}
    </div>
    <div className={`text-2xl md:text-3xl font-semibold tracking-tight ${positive === false ? "text-red-500" : positive === true ? "text-green-500" : ""}`}>{value}</div>
    {sub && <div className="text-xs text-secondary-auvra mt-1">{sub}</div>}
  </Card>
);

const PanelHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
      {subtitle && <p className="text-secondary-auvra text-sm mt-1">{subtitle}</p>}
    </div>
    {actions}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-secondary-auvra mb-2">{label}</span>
    {children}
  </label>
);

const Input = (props) => (
  <input {...props} className={`w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue ${props.className || ""}`} />
);

const Select = ({ children, ...p }) => (
  <select {...p} className={`w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue ${p.className || ""}`}>
    {children}
  </select>
);

const Btn = ({ children, variant = "primary", ...p }) => {
  const cls = variant === "primary"
    ? "bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white"
    : "border border-white/15 hover:border-white/40 text-white";
  return <button {...p} className={`${cls} font-medium px-5 py-3 rounded-full text-sm transition-all ${p.className || ""}`}>{children}</button>;
};

const Chip = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "bg-auvra-blue/10 border-auvra-blue/30 text-auvra-blue",
    green: "bg-green-500/10 border-green-500/30 text-green-400",
    yellow: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",
    red: "bg-red-500/10 border-red-500/30 text-red-400",
    gray: "bg-white/5 border-white/15 text-secondary-auvra",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
};

// =========================
//  1. DASHBOARD (Overview)
// =========================
export function DashboardPanel({ ctx }) {
  const { user, markets, portfolio, priceMap } = ctx;
  const holdingsValue = portfolio.holdings.reduce((s, h) => s + h.units * (priceMap[h.coin_id]?.price || 0), 0);
  const costBasis = portfolio.holdings.reduce((s, h) => s + h.cost_basis, 0);
  const totalValue = portfolio.cash + holdingsValue;
  const pnl = holdingsValue - costBasis;
  const topMovers = [...markets].sort((a, b) => b.change_24h - a.change_24h).slice(0, 5);

  const perfData = useMemo(() => {
    // build a 7d aggregate line from sparklines
    const len = Math.min(...(markets.map((m) => (m.sparkline || []).length).filter(Boolean)), 48) || 24;
    const pts = [];
    for (let i = 0; i < len; i++) {
      let v = 0;
      for (const h of portfolio.holdings) {
        const spark = priceMap[h.coin_id]?.sparkline || [];
        const p = spark[i] ?? priceMap[h.coin_id]?.price ?? 0;
        v += h.units * p;
      }
      pts.push({ t: i, v: v + portfolio.cash });
    }
    return pts;
  }, [markets, portfolio, priceMap]);

  return (
    <div>
      <PanelHeader title={`Welcome back, ${user.name.split(" ")[0]}`} subtitle="Your institutional investor cockpit." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-total" label="Portfolio Value" value={`$${fmt(totalValue)}`} sub={`+$${fmt(Math.max(0, pnl))} lifetime`} />
        <Stat testid="stat-cash" label="Available Cash" value={`$${fmt(portfolio.cash)}`} icon={Wallet} />
        <Stat testid="stat-invested" label="Invested" value={`$${fmt(holdingsValue)}`} />
        <Stat testid="stat-pnl" label="Total P/L" value={`${pnl >= 0 ? "+" : "-"}$${fmt(Math.abs(pnl))}`} positive={pnl >= 0} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" testid="overview-chart">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="font-medium">Portfolio Performance</h3>
            <span className="text-xs text-secondary-auvra">Live · last 7d</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={perfData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} fill="url(#pv)" />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }} labelFormatter={() => ""} formatter={(v) => [`$${fmt(v)}`, "Portfolio"]} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card testid="top-movers">
          <h3 className="font-medium mb-4">Top Movers 24h</h3>
          <div className="space-y-3">
            {topMovers.map((m) => (
              <div key={m.id} className="flex items-center gap-3">
                <img src={m.image} alt="" className="w-7 h-7 rounded-full" />
                <div className="flex-1">
                  <div className="text-sm">{m.symbol}</div>
                  <div className="text-xs text-secondary-auvra">${fmt(m.price)}</div>
                </div>
                <div className={`text-sm ${m.change_24h >= 0 ? "text-green-500" : "text-red-500"}`}>
                  {m.change_24h >= 0 ? "+" : ""}{fmt(m.change_24h)}%
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  2. PORTFOLIO
// =========================
export function PortfolioPanel({ ctx }) {
  const { portfolio, priceMap } = ctx;
  const rows = portfolio.holdings.map((h) => {
    const price = priceMap[h.coin_id]?.price || 0;
    const value = h.units * price;
    const gain = value - h.cost_basis;
    const gainPct = h.cost_basis > 0 ? (gain / h.cost_basis) * 100 : 0;
    return { ...h, price, value, gain, gainPct };
  }).sort((a, b) => b.value - a.value);

  const pie = rows.map((r, i) => ({ name: r.symbol, value: r.value, color: ["#2563EB", "#22c55e", "#eab308", "#a855f7", "#ec4899", "#06b6d4"][i % 6] }));
  const totalVal = rows.reduce((s, r) => s + r.value, 0) || 1;

  return (
    <div>
      <PanelHeader title="Portfolio" subtitle="Complete breakdown of your holdings, exposure and unrealized P/L." />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" testid="portfolio-holdings">
          <h3 className="font-medium mb-4">Holdings</h3>
          {rows.length === 0 ? (
            <p className="text-secondary-auvra text-sm">No holdings yet. Head to Marketplace to invest.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-secondary-auvra border-b border-white/5">
                    <th className="py-3">Asset</th><th>Units</th><th>Avg Cost</th><th>Price</th><th>Value</th><th>P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.coin_id} className="border-b border-white/5 last:border-0">
                      <td className="py-3 font-medium">{r.symbol}</td>
                      <td>{fmt(r.units, 4)}</td>
                      <td>${fmt(r.cost_basis / r.units || 0)}</td>
                      <td>${fmt(r.price)}</td>
                      <td className="font-mono">${fmt(r.value)}</td>
                      <td className={r.gain >= 0 ? "text-green-500" : "text-red-500"}>
                        {r.gain >= 0 ? "+" : ""}${fmt(r.gain)} <span className="text-xs">({fmt(r.gainPct)}%)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card testid="portfolio-allocation">
          <h3 className="font-medium mb-4">Allocation</h3>
          {rows.length === 0 ? (
            <p className="text-secondary-auvra text-sm">Invest to see allocation breakdown.</p>
          ) : (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} innerRadius={45} outerRadius={80} paddingAngle={2} dataKey="value">
                      {pie.map((p, i) => <Cell key={i} fill={p.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-4">
                {pie.map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} /> {p.name}</span>
                    <span>{fmt((p.value / totalVal) * 100)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// =========================
//  3. MARKETPLACE (Crypto + Tokenized RWA offerings)
// =========================
export function MarketplacePanel({ ctx, onInvest, onOpenAsset }) {
  const { markets } = ctx;
  const [offerings, setOfferings] = useState([]);
  const [region, setRegion] = useState("all");
  const [tab, setTab] = useState("crypto");

  useEffect(() => {
    api.get("/offerings", { params: region !== "all" ? { region } : {} })
      .then((r) => setOfferings(r.data)).catch(() => setOfferings([]));
  }, [region]);

  return (
    <div>
      <PanelHeader title="Marketplace" subtitle="Live crypto markets and curated tokenized Real World Asset offerings." />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="marketplace-tabs">
        {[["crypto", "Crypto Markets"], ["rwa", "RWA Offerings"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === k ? "bg-auvra-blue border-auvra-blue text-white" : "border-white/15 text-secondary-auvra hover:border-white/40"}`}>
            {l}
          </button>
        ))}
        {tab === "rwa" && (
          <Select value={region} onChange={(e) => setRegion(e.target.value)} className="!w-auto ml-auto" data-testid="rwa-region-filter">
            <option value="all">All regions</option>
            <option value="Europe">Europe</option>
            <option value="APAC">APAC</option>
            <option value="Global">Global</option>
          </Select>
        )}
      </div>

      {tab === "crypto" ? (
        <Card testid="crypto-markets">
          <div className="space-y-2">
            {markets.length === 0 && <p className="text-secondary-auvra text-sm py-4">Loading markets…</p>}
            {markets.map((m) => (
              <div key={m.id} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`mp-crypto-${m.symbol}`}>
                <img src={m.image} alt="" className="w-8 h-8 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-secondary-auvra">{m.symbol} · {cap(m.market_cap)}</div>
                </div>
                <div className="w-20 h-9 hidden sm:block">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={(m.sparkline || []).map((v) => ({ v }))}>
                      <Area type="monotone" dataKey="v" stroke={m.change_24h >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={1.5} fill="transparent" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-right w-24">
                  <div className="font-mono text-sm">${fmt(m.price, m.price < 5 ? 4 : 2)}</div>
                  <div className={`text-xs ${m.change_24h >= 0 ? "text-green-500" : "text-red-500"}`}>{m.change_24h >= 0 ? "+" : ""}{fmt(m.change_24h)}%</div>
                </div>
                <Btn onClick={() => onInvest(m)} data-testid={`buy-crypto-${m.symbol}`}>Invest</Btn>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="rwa-offerings-grid">
          {offerings.map((o) => (
            <button key={o.id} onClick={() => onOpenAsset(o)} data-testid={`rwa-${o.id}`}
              className="text-left glass rounded-2xl p-6 hover:border-auvra-blue/40 transition-colors border border-white/5">
              <div className="flex gap-4">
                <img src={o.image} alt="" className="w-24 h-24 object-cover rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Chip tone={o.status === "OPEN" ? "green" : "yellow"}>{o.status}</Chip>
                    <Chip tone="gray">{o.class}</Chip>
                  </div>
                  <h4 className="text-lg font-semibold truncate">{o.title}</h4>
                  <p className="text-xs text-secondary-auvra mt-1 line-clamp-2">{o.summary}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5 text-xs">
                <div><div className="text-secondary-auvra">Target Yield</div><div className="text-auvra-blue text-sm font-semibold">{o.target_yield}%</div></div>
                <div><div className="text-secondary-auvra">Term</div><div className="text-sm">{o.term_months}m</div></div>
                <div><div className="text-secondary-auvra">Min Ticket</div><div className="text-sm">${fmt(o.min_ticket, 0)}</div></div>
              </div>
              <div className="mt-4">
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-auvra-blue" style={{ width: `${Math.min(100, (o.raised / o.target) * 100)}%` }} />
                </div>
                <div className="text-[10px] text-secondary-auvra mt-1 flex justify-between"><span>${fmt(o.raised / 1e6, 2)}M raised</span><span>${fmt(o.target / 1e6, 1)}M target</span></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================
//  4. INVESTMENTS (Ordered activity history)
// =========================
export function InvestmentsPanel({ ctx }) {
  const { portfolio, priceMap } = ctx;
  return (
    <div>
      <PanelHeader title="Investments" subtitle="All positions and past investment orders in chronological order." />
      <Card testid="investments-log">
        {portfolio.transactions.length === 0 ? (
          <p className="text-secondary-auvra text-sm">No investment activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-secondary-auvra border-b border-white/5">
                  <th className="py-3">Date</th><th>Asset</th><th>Type</th><th>Units</th><th>Price</th><th>Amount</th><th>Current Value</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.transactions.map((t) => {
                  const currentValue = t.units * (priceMap[t.coin_id]?.price || t.price);
                  return (
                    <tr key={t.id} className="border-b border-white/5 last:border-0">
                      <td className="py-3 text-xs text-secondary-auvra">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="font-medium">{t.symbol}</td>
                      <td><Chip tone="green">BUY</Chip></td>
                      <td>{fmt(t.units, 4)}</td>
                      <td>${fmt(t.price)}</td>
                      <td className="font-mono">${fmt(t.usd_amount)}</td>
                      <td className="font-mono">${fmt(currentValue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// =========================
//  5. ASSET DETAILS
// =========================
export function AssetDetailsPanel({ ctx, activeAsset, setActiveAsset }) {
  const { markets, portfolio, priceMap } = ctx;
  const [chart, setChart] = useState([]);
  const [assetId, setAssetId] = useState(activeAsset || markets[0]?.id || "bitcoin");

  useEffect(() => {
    if (activeAsset) setAssetId(activeAsset);
  }, [activeAsset]);

  useEffect(() => {
    if (!assetId) return;
    api.get(`/crypto/history/${assetId}`, { params: { days: 30 } })
      .then((r) => setChart(r.data.map((p) => ({ t: p.t, v: p.v })))).catch(() => setChart([]));
  }, [assetId]);

  const asset = priceMap[assetId] || markets[0];
  const holding = portfolio.holdings.find((h) => h.coin_id === assetId);

  return (
    <div>
      <PanelHeader
        title="Asset Details"
        subtitle="Deep-dive into any crypto asset — 30-day price action, market stats and your position."
        actions={
          <Select value={assetId} onChange={(e) => { setAssetId(e.target.value); setActiveAsset(null); }} className="!w-auto" data-testid="asset-select">
            {markets.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.symbol})</option>)}
          </Select>
        }
      />
      {asset && (
        <>
          <div className="grid lg:grid-cols-3 gap-4 mb-4">
            <Card>
              <div className="flex items-center gap-3">
                <img src={asset.image} alt="" className="w-10 h-10 rounded-full" />
                <div>
                  <div className="font-semibold">{asset.name}</div>
                  <div className="text-xs text-secondary-auvra">{asset.symbol}</div>
                </div>
              </div>
              <div className="text-3xl font-semibold tracking-tight mt-4">${fmt(asset.price)}</div>
              <div className={`text-sm mt-1 ${asset.change_24h >= 0 ? "text-green-500" : "text-red-500"}`}>{asset.change_24h >= 0 ? "+" : ""}{fmt(asset.change_24h)}% (24h)</div>
            </Card>
            <Stat label="Market Cap" value={cap(asset.market_cap)} />
            <Stat label="24h Volume" value={cap(asset.volume)} />
          </div>
          <Card>
            <h3 className="font-medium mb-4">30-Day Price</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart}>
                  <defs>
                    <linearGradient id="ac" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BLUE} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" hide />
                  <YAxis domain={["auto", "auto"]} hide />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12 }}
                    labelFormatter={(l) => new Date(l).toLocaleDateString()}
                    formatter={(v) => [`$${fmt(v)}`, "Price"]} />
                  <Area type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2} fill="url(#ac)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
          {holding && (
            <Card className="mt-4">
              <h3 className="font-medium mb-4">Your position</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><div className="text-xs text-secondary-auvra mb-1">Units</div><div>{fmt(holding.units, 6)}</div></div>
                <div><div className="text-xs text-secondary-auvra mb-1">Cost Basis</div><div>${fmt(holding.cost_basis)}</div></div>
                <div><div className="text-xs text-secondary-auvra mb-1">Current Value</div><div>${fmt(holding.units * asset.price)}</div></div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// =========================
//  6. WALLET
// =========================
export function WalletPanel({ ctx, setView }) {
  const { wallet } = ctx;
  return (
    <div>
      <PanelHeader title="Wallet" subtitle="Manage cash, deposit crypto and initiate withdrawals." />
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Stat testid="wallet-cash" label="Available Balance" value={`$${fmt(wallet.cash)}`} icon={Wallet} />
        <Stat testid="wallet-deposits" label="Deposits" value={wallet.deposits.length} sub="lifetime" />
        <Stat testid="wallet-withdrawals" label="Withdrawals" value={wallet.withdrawals.length} sub="lifetime" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-3 mb-3"><Download className="w-5 h-5 text-auvra-blue" /><h3 className="font-medium">Deposit</h3></div>
          <p className="text-sm text-secondary-auvra mb-4">Fund your account instantly with crypto via BNB Smart Chain or Tron.</p>
          <Btn onClick={() => setView("deposit")} data-testid="go-deposit">Go to Deposit</Btn>
        </Card>
        <Card>
          <div className="flex items-center gap-3 mb-3"><Upload className="w-5 h-5 text-auvra-blue" /><h3 className="font-medium">Withdraw</h3></div>
          <p className="text-sm text-secondary-auvra mb-4">Withdraw funds to any BEP20 or TRC20 address. KYC verification required.</p>
          <Btn variant="secondary" onClick={() => setView("withdraw")} data-testid="go-withdraw">Go to Withdraw</Btn>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  7. DEPOSIT
// =========================
export function DepositPanel({ ctx, refresh }) {
  const { wallet } = ctx;
  const [network, setNetwork] = useState("USDT_BEP20");
  const [amount, setAmount] = useState("");
  const [txHash, setTxHash] = useState("");
  const [busy, setBusy] = useState(false);
  const addr = wallet.addresses?.[network];

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/wallet/deposit", {
        network: addr.network, asset: addr.asset || "BNB",
        amount: Number(amount), tx_hash: txHash || null,
      });
      toast.success("Deposit submitted — pending confirmations");
      setAmount(""); setTxHash("");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  return (
    <div>
      <PanelHeader title="Deposit" subtitle="Deposit crypto to fund your AUVRA account. Only send the selected asset to the address below." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card testid="deposit-address-card">
          <Field label="Network / Asset">
            <Select value={network} onChange={(e) => setNetwork(e.target.value)} data-testid="deposit-network">
              <option value="USDT_BEP20">USDT · BEP20 (BNB Smart Chain)</option>
              <option value="USDT_TRC20">USDT · TRC20 (Tron)</option>
              <option value="BNB">BNB · BEP20 (BNB Smart Chain)</option>
            </Select>
          </Field>
          {addr && (
            <div className="mt-6 space-y-3">
              <div>
                <div className="kicker mb-2">Wallet Address</div>
                <div className="flex gap-2">
                  <code data-testid="deposit-address" className="flex-1 truncate rounded-xl border border-white/10 bg-black px-4 py-3 text-xs font-mono text-auvra-blue">{addr.address}</code>
                  <button onClick={() => copy(addr.address)} data-testid="copy-deposit-address" className="flex items-center gap-2 border border-white/15 hover:border-auvra-blue rounded-xl px-4 text-sm">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-secondary-auvra">Chain</div><div>{addr.chain}</div></div>
                <div><div className="text-secondary-auvra">Confirmations</div><div>{addr.min_confirmations}</div></div>
                <div><div className="text-secondary-auvra">Min Deposit</div><div>{addr.min_deposit} {addr.asset || "BNB"}</div></div>
                <div><div className="text-secondary-auvra">Fee</div><div>Network gas only</div></div>
              </div>
              <div className="text-xs text-yellow-400/90 bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3">
                ⚠ Send only {addr.asset || "BNB"} on {addr.network}. Cross-network transfers may be permanently lost.
              </div>
            </div>
          )}
        </Card>
        <Card testid="deposit-record-card">
          <h3 className="font-medium mb-4">Record your on-chain transfer</h3>
          <div className="space-y-4">
            <Field label={`Amount (${addr?.asset || "BNB"})`}>
              <Input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deposit-amount" />
            </Field>
            <Field label="Transaction hash (optional)">
              <Input placeholder="0x…" value={txHash} onChange={(e) => setTxHash(e.target.value)} data-testid="deposit-tx-hash" />
            </Field>
            <Btn onClick={submit} disabled={busy} data-testid="submit-deposit">{busy ? "Submitting…" : "Submit Deposit"}</Btn>
            <p className="text-xs text-secondary-auvra">Your deposit will be credited after on-chain confirmations by our custody team.</p>
          </div>
        </Card>
      </div>
      <Card className="mt-4" testid="deposit-history">
        <h3 className="font-medium mb-4">Recent deposits</h3>
        {wallet.deposits.length === 0 ? <p className="text-sm text-secondary-auvra">No deposits yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
              <th className="py-2">Date</th><th>Network</th><th>Asset</th><th>Amount</th><th>Status</th><th>Tx</th></tr></thead>
            <tbody>{wallet.deposits.map((d) => (
              <tr key={d.id} className="border-b border-white/5 last:border-0">
                <td className="py-2 text-xs text-secondary-auvra">{new Date(d.created_at).toLocaleString()}</td>
                <td>{d.network}</td><td>{d.asset}</td><td>{fmt(d.amount)}</td>
                <td><Chip tone="yellow">{d.status}</Chip></td>
                <td className="text-xs font-mono truncate max-w-[160px]">{d.tx_hash || "—"}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// =========================
//  8. WITHDRAW
// =========================
export function WithdrawPanel({ ctx, refresh }) {
  const { wallet, kyc } = ctx;
  const [network, setNetwork] = useState("BEP20");
  const [asset, setAsset] = useState("USDT");
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const kycOk = kyc?.status === "APPROVED";

  const submit = async () => {
    if (!kycOk) { toast.error("Complete KYC to withdraw"); return; }
    if (!address) { toast.error("Enter destination address"); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { network, asset, amount: Number(amount), address });
      toast.success("Withdrawal requested");
      setAmount(""); setAddress("");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div>
      <PanelHeader title="Withdraw" subtitle="Send funds to a BEP20 or TRC20 address. KYC verification is required." />
      {!kycOk && (
        <div className="glass rounded-2xl p-6 mb-4 border border-yellow-500/30" data-testid="kyc-warning">
          <div className="flex items-center gap-2 text-yellow-400 mb-2"><Shield className="w-4 h-4" /> KYC required</div>
          <p className="text-sm text-secondary-auvra">Complete identity verification to unlock withdrawals.</p>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <Field label="Network"><Select value={network} onChange={(e) => setNetwork(e.target.value)} data-testid="withdraw-network"><option>BEP20</option><option>TRC20</option></Select></Field>
          <div className="mt-4"><Field label="Asset"><Select value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="withdraw-asset"><option>USDT</option>{network === "BEP20" && <option>BNB</option>}</Select></Field></div>
          <div className="mt-4"><Field label="Destination Address"><Input placeholder={network === "BEP20" ? "0x…" : "T…"} value={address} onChange={(e) => setAddress(e.target.value)} data-testid="withdraw-address" /></Field></div>
          <div className="mt-4"><Field label="Amount"><Input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" /></Field></div>
          <div className="text-xs text-secondary-auvra mt-2">Available: <span className="text-white">${fmt(wallet.cash)}</span></div>
          <div className="mt-6"><Btn onClick={submit} disabled={busy || !kycOk} data-testid="submit-withdraw">{busy ? "Submitting…" : "Submit Withdrawal"}</Btn></div>
        </Card>
        <Card testid="withdraw-history">
          <h3 className="font-medium mb-4">Recent withdrawals</h3>
          {wallet.withdrawals.length === 0 ? <p className="text-sm text-secondary-auvra">No withdrawals yet.</p> : (
            <div className="space-y-3">
              {wallet.withdrawals.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-3 last:border-0">
                  <div>
                    <div>{fmt(w.amount)} {w.asset} <span className="text-xs text-secondary-auvra">via {w.network}</span></div>
                    <div className="text-xs font-mono text-secondary-auvra truncate max-w-[240px]">{w.address}</div>
                  </div>
                  <Chip tone="yellow">{w.status}</Chip>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// =========================
//  9. TRANSACTIONS
// =========================
export function TransactionsPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/transactions").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);
  return (
    <div>
      <PanelHeader title="Transactions" subtitle="Unified ledger of all deposits, withdrawals and investment activity." actions={
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="!w-auto" data-testid="tx-filter">
          <option value="all">All</option><option value="deposit">Deposits</option>
          <option value="withdraw">Withdrawals</option><option value="invest">Investments</option>
        </Select>
      } />
      <Card testid="tx-log">
        {filtered.length === 0 ? <p className="text-secondary-auvra text-sm">No transactions yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
              <th className="py-3">Date</th><th>Kind</th><th>Detail</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>{filtered.map((t, i) => (
              <tr key={t.id || i} className="border-b border-white/5 last:border-0">
                <td className="py-3 text-xs text-secondary-auvra">{new Date(t.created_at).toLocaleString()}</td>
                <td><Chip tone={t.kind === "deposit" ? "green" : t.kind === "withdraw" ? "yellow" : "blue"}>{t.kind}</Chip></td>
                <td>{t.symbol || `${t.asset || "USDT"} · ${t.network || ""}`}</td>
                <td className="font-mono">${fmt(t.usd_amount || t.amount || 0)}</td>
                <td>{t.status || "COMPLETED"}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// =========================
//  10. DIVIDENDS — see DividendsPanel below (real subscription-based)
// =========================

// =========================
//  11. STAKING
// =========================
export function StakingPanel() {
  const [data, setData] = useState({ plans: [], positions: [], total_staked: 0 });
  const [plan, setPlan] = useState("30D");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/staking").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const stake = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter amount"); return; }
    setBusy(true);
    try { await api.post("/staking/stake", { plan, amount: Number(amount) }); toast.success("Staked"); setAmount(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div>
      <PanelHeader title="Staking" subtitle="Lock capital into fixed-term staking plans to earn compounding APY." />
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        {data.plans.map((p) => (
          <Card key={p.plan} testid={`plan-${p.plan}`}>
            <div className="text-xs text-secondary-auvra uppercase tracking-wider">{p.plan}</div>
            <div className="text-3xl font-semibold text-auvra-blue mt-2">{p.apy}%</div>
            <div className="text-xs text-secondary-auvra mt-1">APY · {p.lock_days} days lock</div>
            <div className="text-xs mt-3">Min: ${fmt(p.min, 0)}</div>
          </Card>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <h3 className="font-medium mb-4">Stake now</h3>
          <Field label="Plan"><Select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="stake-plan">
            {data.plans.map((p) => <option key={p.plan} value={p.plan}>{p.plan} — {p.apy}% APY</option>)}
          </Select></Field>
          <div className="mt-4"><Field label="Amount"><Input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="stake-amount" /></Field></div>
          <div className="mt-6"><Btn onClick={stake} disabled={busy} data-testid="submit-stake">{busy ? "Staking…" : "Stake"}</Btn></div>
        </Card>
        <Card className="lg:col-span-2" testid="staking-positions">
          <h3 className="font-medium mb-4">Your staked positions <span className="text-xs text-secondary-auvra ml-2">Total: ${fmt(data.total_staked)}</span></h3>
          {data.positions.length === 0 ? <p className="text-sm text-secondary-auvra">Not staked yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
                <th className="py-3">Plan</th><th>APY</th><th>Amount</th><th>Matures</th><th>Status</th></tr></thead>
              <tbody>{data.positions.map((p) => (
                <tr key={p.id} className="border-b border-white/5 last:border-0">
                  <td className="py-3">{p.plan}</td><td>{p.apy}%</td><td>${fmt(p.amount)}</td>
                  <td>{new Date(p.matures_at).toLocaleDateString()}</td>
                  <td><Chip tone="green">{p.status}</Chip></td>
                </tr>))}</tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

// =========================
//  12. AUTO INVEST
// =========================
export function AutoInvestPanel({ ctx }) {
  const { markets } = ctx;
  const [cfg, setCfg] = useState({ enabled: false, frequency: "weekly", amount: 100, allocations: {} });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auto-invest").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const setAlloc = (id, v) => {
    const n = Number(v) || 0;
    setCfg((c) => ({ ...c, allocations: { ...c.allocations, [id]: n } }));
  };
  const total = Object.values(cfg.allocations || {}).reduce((s, n) => s + Number(n || 0), 0);

  const save = async () => {
    if (cfg.enabled && Math.round(total) !== 100) { toast.error("Allocations must total 100%"); return; }
    setBusy(true);
    try { await api.post("/auto-invest", cfg); toast.success("Auto-invest saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div>
      <PanelHeader title="Auto Invest" subtitle="Automatically deploy capital on a schedule across a diversified allocation." />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <Field label="Enabled">
            <button onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))} data-testid="auto-toggle"
              className={`w-14 h-8 rounded-full transition-colors ${cfg.enabled ? "bg-auvra-blue" : "bg-white/10"} relative`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </Field>
          <div className="mt-4"><Field label="Frequency"><Select value={cfg.frequency} onChange={(e) => setCfg((c) => ({ ...c, frequency: e.target.value }))} data-testid="auto-frequency">
            <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
          </Select></Field></div>
          <div className="mt-4"><Field label="Amount per cycle (USD)"><Input type="number" value={cfg.amount} onChange={(e) => setCfg((c) => ({ ...c, amount: Number(e.target.value) || 0 }))} data-testid="auto-amount" /></Field></div>
          <div className="mt-6"><Btn onClick={save} disabled={busy} data-testid="auto-save">{busy ? "Saving…" : "Save"}</Btn></div>
        </Card>
        <Card className="lg:col-span-2" testid="auto-allocations">
          <h3 className="font-medium mb-2">Allocations</h3>
          <p className="text-xs text-secondary-auvra mb-4">Split each cycle across assets. Must sum to 100%. Current total: <span className={total === 100 ? "text-green-500" : "text-yellow-400"}>{total}%</span></p>
          <div className="space-y-3">
            {markets.slice(0, 6).map((m) => (
              <div key={m.id} className="flex items-center gap-3">
                <img src={m.image} alt="" className="w-6 h-6 rounded-full" />
                <div className="flex-1 text-sm">{m.symbol}</div>
                <Input type="number" min="0" max="100" step="5" className="!w-24" value={cfg.allocations?.[m.id] || 0} onChange={(e) => setAlloc(m.id, e.target.value)} data-testid={`alloc-${m.symbol}`} />
                <span className="text-xs text-secondary-auvra w-4">%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  13. REPORTS
// =========================
export function ReportsPanel() {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((r) => setRep(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PanelHeader title="Reports" subtitle="Download and view your account statements and tax exports." />
      {rep && (
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <Stat label="Cash" value={`$${fmt(rep.cash)}`} />
          <Stat label="Invested" value={`$${fmt(rep.invested)}`} />
          <Stat label="Holdings" value={rep.holdings_count} />
          <Stat label="Transactions" value={rep.transactions_count} />
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          ["Monthly Statement", "Portfolio snapshot & activity for the current month.", "monthly-statement"],
          ["Annual Report", "Full year statement with realized gains and dividends.", "annual-report"],
          ["Tax Export (CSV)", "Downloadable tax-ready transaction ledger.", "tax-csv"],
        ].map(([t, d, k]) => (
          <Card key={k} testid={`report-${k}`}>
            <FileText className="w-5 h-5 text-auvra-blue mb-3" />
            <h4 className="font-medium">{t}</h4>
            <p className="text-xs text-secondary-auvra mt-1 mb-4">{d}</p>
            <Btn variant="secondary" onClick={() => toast.success("Report queued — you'll get an email shortly")}>Generate</Btn>
          </Card>
        ))}
      </div>
    </div>
  );
}

// =========================
//  14. KYC
// =========================
export function KycPanel({ refresh }) {
  const [state, setState] = useState({ status: "NOT_SUBMITTED" });
  const [form, setForm] = useState({ full_name: "", country: "", id_type: "passport", id_number: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const load = () => api.get("/kyc").then((r) => setState(r.data)).catch(() => {});
  const loadDocs = () => api.get("/kyc/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); loadDocs(); }, []);

  const submit = async () => {
    if (!form.full_name || !form.country || !form.id_number) { toast.error("Please complete all required fields"); return; }
    setBusy(true);
    try { await api.post("/kyc", form); toast.success("KYC submitted for review"); load(); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const demoApprove = async () => {
    try { await api.post("/kyc/approve"); toast.success("Identity verified (demo)"); load(); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(kind);
    const fd = new FormData();
    fd.append("kind", kind); fd.append("file", file);
    try { await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${kind.replace("_", " ")} uploaded`); loadDocs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setUploading(null);
  };

  const statusChip = { NOT_SUBMITTED: "gray", PENDING_REVIEW: "yellow", APPROVED: "green" }[state.status] || "gray";
  const docBy = (kind) => docs.find((d) => d.kind === kind);

  return (
    <div>
      <PanelHeader title="KYC & Compliance" subtitle="Identity verification unlocks withdrawals and premium tokenized-asset offerings." actions={<Chip tone={statusChip}>{state.status.replace("_", " ")}</Chip>} />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <h3 className="font-medium mb-4">1. Submit your identity</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full Legal Name *"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="kyc-name" /></Field>
            <Field label="Country of Residence *"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="kyc-country" /></Field>
            <Field label="ID Type"><Select value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} data-testid="kyc-id-type">
              <option value="passport">Passport</option><option value="national_id">National ID</option><option value="drivers_license">Driver&apos;s License</option>
            </Select></Field>
            <Field label="ID Number *"><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="kyc-id-number" /></Field>
            <div className="md:col-span-2"><Field label="Residential Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="kyc-address" /></Field></div>
          </div>
          <div className="flex gap-3 mt-6">
            <Btn onClick={submit} disabled={busy || state.status === "APPROVED"} data-testid="kyc-submit">{busy ? "Submitting…" : state.status === "APPROVED" ? "Verified" : "Submit for Review"}</Btn>
            {state.status === "PENDING_REVIEW" && <Btn variant="secondary" onClick={demoApprove} data-testid="kyc-demo-approve">Instant demo approval</Btn>}
          </div>

          <div className="mt-8 pt-6 border-t border-white/5">
            <h3 className="font-medium mb-1">2. Upload verification documents</h3>
            <p className="text-xs text-secondary-auvra mb-5">Accepted: JPG · PNG · WEBP · PDF · Max 10MB each. All files are encrypted at rest.</p>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { kind: "id_document", label: "Government-issued ID", desc: "Passport / national ID / driver's license" },
                { kind: "address_proof", label: "Proof of Address", desc: "Utility bill / bank statement · last 3 months" },
                { kind: "selfie", label: "Selfie / Liveness", desc: "Clear photo holding your ID" },
              ].map((u) => {
                const existing = docBy(u.kind);
                return (
                  <label key={u.kind} className={`glass rounded-2xl p-5 cursor-pointer border transition-colors ${existing ? "border-green-500/40" : "border-white/5 hover:border-auvra-blue/30"}`} data-testid={`upload-${u.kind}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {existing ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Upload className="w-4 h-4 text-auvra-blue" />}
                      <span className="text-sm font-medium">{u.label}</span>
                    </div>
                    <p className="text-xs text-secondary-auvra mb-3">{u.desc}</p>
                    {existing ? (
                      <div className="text-xs text-secondary-auvra">
                        <div className="truncate text-white/80">{existing.filename}</div>
                        <div>{(existing.size / 1024).toFixed(1)} KB · {new Date(existing.uploaded_at).toLocaleDateString()}</div>
                        <div className="mt-2 text-auvra-blue">Replace file</div>
                      </div>
                    ) : (
                      <div className="text-xs text-auvra-blue">{uploading === u.kind ? "Uploading…" : "Click to upload"}</div>
                    )}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" className="hidden"
                      onChange={(e) => upload(u.kind, e.target.files?.[0])} data-testid={`file-${u.kind}`} />
                  </label>
                );
              })}
            </div>
          </div>
        </Card>
        <Card>
          <h3 className="font-medium mb-3">What we require</h3>
          <ul className="space-y-2 text-sm text-secondary-auvra">
            {["Valid government-issued ID", "Proof of address (utility/bank)", "Selfie or liveness check", "Source of funds attestation"].map((x) => (
              <li key={x} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-auvra-blue flex-shrink-0 mt-0.5" /> {x}</li>
            ))}
          </ul>
          <p className="text-xs text-secondary-auvra mt-4">All information is encrypted and reviewed by AUVRA&apos;s compliance team in accordance with applicable regulations.</p>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  15. NOTIFICATIONS
// =========================
export function NotificationsPanel({ refresh }) {
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markAll = async () => { await api.post("/notifications/read"); load(); refresh(); toast.success("All notifications marked as read"); };
  return (
    <div>
      <PanelHeader title="Notifications" subtitle={`${data.unread} unread`} actions={<Btn variant="secondary" onClick={markAll} data-testid="mark-all-read">Mark all read</Btn>} />
      <Card testid="notif-list">
        {data.items.length === 0 ? <p className="text-sm text-secondary-auvra">You&apos;re all caught up.</p> : (
          <div className="space-y-3">
            {data.items.map((n) => (
              <div key={n.id} className={`flex gap-3 p-4 rounded-xl border border-white/5 ${n.read ? "opacity-70" : "bg-white/[0.02]"}`}>
                <BellRing className="w-4 h-4 mt-1 text-auvra-blue flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{n.title}</div>
                  <div className="text-xs text-secondary-auvra mt-1">{n.body}</div>
                  <div className="text-[10px] text-secondary-auvra mt-2">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && <Chip tone="blue">NEW</Chip>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// =========================
//  16. SUPPORT
// =========================
export function SupportPanel() {
  const [f, setF] = useState({ name: "", email: "", inquiry_type: "Support", region: "global", message: "", company: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email || !f.message) { toast.error("Please complete the form"); return; }
    setBusy(true);
    try { await api.post("/contact", f); toast.success("Support ticket created — we'll respond within 4 hours"); setF({ ...f, message: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div>
      <PanelHeader title="Support" subtitle="Reach the AUVRA support desk 24/7. Institutional clients get direct-relationship coverage." />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <h3 className="font-medium mb-4">Open a support ticket</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sup-name" /></Field>
            <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="sup-email" /></Field>
            <Field label="Region"><Select value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} data-testid="sup-region">
              <option value="emea">EMEA</option><option value="apac">APAC</option><option value="americas">Americas</option><option value="global">Global</option>
            </Select></Field>
            <Field label="Topic"><Select value={f.inquiry_type} onChange={(e) => setF({ ...f, inquiry_type: e.target.value })} data-testid="sup-topic">
              <option>Support</option><option>Account</option><option>Deposit / Withdrawal</option><option>Compliance</option><option>Institutional</option>
            </Select></Field>
            <div className="md:col-span-2"><Field label="Message">
              <textarea rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="sup-message"
                className="w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue" />
            </Field></div>
          </div>
          <div className="mt-6"><Btn onClick={submit} disabled={busy} data-testid="sup-submit">{busy ? "Sending…" : "Send Ticket"}</Btn></div>
        </Card>
        <Card>
          <LifeBuoy className="w-5 h-5 text-auvra-blue mb-3" />
          <h3 className="font-medium">Direct channels</h3>
          <ul className="text-sm text-secondary-auvra mt-3 space-y-2">
            <li>support@auvrarwa.com</li>
            <li>emea@auvrarwa.com</li>
            <li>apac@auvrarwa.com</li>
            <li>americas@auvrarwa.com</li>
          </ul>
          <p className="text-xs text-secondary-auvra mt-6">Institutional response SLA: <span className="text-white">4 hours</span></p>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  17. PROFILE
// =========================
export function ProfilePanel({ ctx, refresh }) {
  const { user } = ctx;
  const [f, setF] = useState({ name: user.name || "", picture: user.picture || "", phone: "", country: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/profile", f); toast.success("Profile updated"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div>
      <PanelHeader title="Profile" subtitle="Manage your personal information and preferences." />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card testid="profile-summary">
          <div className="flex items-center gap-4">
            {f.picture ? <img src={f.picture} alt="" className="w-16 h-16 rounded-full object-cover" /> :
              <div className="w-16 h-16 rounded-full bg-auvra-blue/20 flex items-center justify-center"><UserIcon className="w-6 h-6 text-auvra-blue" /></div>}
            <div>
              <div className="font-semibold">{user.name}</div>
              <div className="text-xs text-secondary-auvra">{user.email}</div>
              <div className="text-[10px] mt-1"><Chip tone="blue">{user.provider}</Chip></div>
            </div>
          </div>
        </Card>
        <Card className="lg:col-span-2">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Display Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="prof-name" /></Field>
            <Field label="Picture URL"><Input value={f.picture} onChange={(e) => setF({ ...f, picture: e.target.value })} data-testid="prof-picture" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="prof-phone" /></Field>
            <Field label="Country"><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="prof-country" /></Field>
          </div>
          <div className="mt-6"><Btn onClick={save} disabled={busy} data-testid="prof-save">{busy ? "Saving…" : "Save"}</Btn></div>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  18. SECURITY
// =========================
export function SecurityPanel({ ctx }) {
  const { user } = ctx;
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try { await api.post("/security/password", f); toast.success("Password updated"); setF({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div>
      <PanelHeader title="Security" subtitle="Passwords, 2FA and device sessions." />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <h3 className="font-medium mb-4">Change password</h3>
          {user.provider !== "email" ? (
            <p className="text-sm text-secondary-auvra">This account signs in with {user.provider}. Password changes are handled by your identity provider.</p>
          ) : (
            <>
              <Field label="Current password"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="sec-current" /></Field>
              <div className="mt-4"><Field label="New password"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="sec-new" /></Field></div>
              <div className="mt-6"><Btn onClick={submit} disabled={busy} data-testid="sec-submit">{busy ? "Updating…" : "Update Password"}</Btn></div>
            </>
          )}
        </Card>
        <Card>
          <ShieldCheck className="w-5 h-5 text-auvra-blue mb-3" />
          <h3 className="font-medium">Security tips</h3>
          <ul className="text-sm text-secondary-auvra mt-3 space-y-2 list-disc list-inside">
            <li>Use a unique password & password manager</li>
            <li>Enable 2FA once available</li>
            <li>Never share your session token</li>
            <li>Verify emails come from @auvrarwa.com</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

// =========================
//  19. SETTINGS
// =========================
const SettingsToggle = ({ on, onToggle, label, desc, testid }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
    <div><div className="text-sm">{label}</div><div className="text-xs text-secondary-auvra">{desc}</div></div>
    <button onClick={onToggle} data-testid={testid}
      className={`w-12 h-7 rounded-full transition-colors ${on ? "bg-auvra-blue" : "bg-white/10"} relative`}>
      <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  </div>
);

export function SettingsPanel() {
  const [s, setS] = useState({ email_notifications: true, price_alerts: true, marketing: false, two_factor: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS((x) => ({ ...x, ...r.data }))).catch(() => {}); }, []);
  const save = async () => { setBusy(true); try { await api.put("/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false); };
  const flip = (k) => setS((x) => ({ ...x, [k]: !x[k] }));
  return (
    <div>
      <PanelHeader title="Settings" subtitle="Preferences for how AUVRA communicates with you." />
      <Card testid="settings-card">
        <SettingsToggle on={s.email_notifications} onToggle={() => flip("email_notifications")} label="Email notifications" desc="Deposits, withdrawals, dividends and important account events." testid="toggle-email_notifications" />
        <SettingsToggle on={s.price_alerts} onToggle={() => flip("price_alerts")} label="Price alerts" desc="Notify when a watched asset moves significantly." testid="toggle-price_alerts" />
        <SettingsToggle on={s.marketing} onToggle={() => flip("marketing")} label="Marketing emails" desc="Product updates and offering announcements." testid="toggle-marketing" />
        <SettingsToggle on={s.two_factor} onToggle={() => flip("two_factor")} label="Two-factor authentication" desc="Extra security via TOTP (coming soon)." testid="toggle-two_factor" />
        <div className="mt-6"><Btn onClick={save} disabled={busy} data-testid="settings-save">{busy ? "Saving…" : "Save"}</Btn></div>
      </Card>
    </div>
  );
}

// =========================
//  10. DIVIDENDS (real, from subscriptions)
// =========================
export function DividendsPanel() {
  const [data, setData] = useState({ total_paid: 0, total_scheduled: 0, next_due: null, entries: [] });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/dividends").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const settle = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/dividends/settle"); toast.success(r.credited > 0 ? `$${fmt(r.credited)} credited` : "No due payouts"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div>
      <PanelHeader title="Dividends" subtitle="Real distributions from your subscribed tokenized offerings. Payouts settle to your wallet on their due date."
        actions={<Btn variant="secondary" onClick={settle} disabled={busy} data-testid="settle-btn">{busy ? "Settling…" : "Settle Due Payouts"}</Btn>} />
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Stat label="Total Paid" value={`$${fmt(data.total_paid)}`} icon={Coins} testid="div-paid" />
        <Stat label="Scheduled" value={`$${fmt(data.total_scheduled)}`} testid="div-scheduled" />
        <Stat label="Next Due" value={data.next_due ? new Date(data.next_due).toLocaleDateString() : "—"} testid="div-next-due" />
      </div>
      <Card testid="dividends-table">
        {data.entries.length === 0 ? <p className="text-sm text-secondary-auvra">Subscribe to a tokenized offering in the Marketplace to start earning distributions.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
                <th className="py-3">Due</th><th>Offering</th><th>Period</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>{data.entries.map((e) => (
                <tr key={e.id} className="border-b border-white/5 last:border-0">
                  <td className="py-3 text-xs text-secondary-auvra">{new Date(e.due_at).toLocaleDateString()}</td>
                  <td className="max-w-[220px] truncate">{e.name}</td>
                  <td>{e.period}</td>
                  <td className="font-mono">${fmt(e.amount)}</td>
                  <td><Chip tone={e.status === "PAID" ? "green" : "gray"}>{e.status}</Chip></td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// =========================
//  10b. INVESTMENTS extended — Subscriptions (list)
// =========================
export function SubscriptionsPanel() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PanelHeader title="My Subscriptions" subtitle="Your active tokenized asset positions and their payout schedules." />
      {subs.length === 0 ? (
        <Card><p className="text-sm text-secondary-auvra">You have no active subscriptions. Head to Marketplace → RWA Offerings.</p></Card>
      ) : (
        <div className="grid gap-4">
          {subs.map((s) => {
            const paid = (s.schedule || []).filter((e) => e.status === "PAID").length;
            const total = (s.schedule || []).length;
            return (
              <Card key={s.id} testid={`sub-${s.id}`}>
                <div className="flex flex-wrap gap-4 items-start">
                  <img src={s.image} alt="" className="w-24 h-24 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 flex-wrap mb-1">
                      <Chip tone="green">{s.status}</Chip>
                      <Chip tone="gray">{s.offering_class}</Chip>
                    </div>
                    <h4 className="text-lg font-semibold">{s.offering_title}</h4>
                    <div className="grid grid-cols-4 gap-4 mt-3 text-xs">
                      <div><div className="text-secondary-auvra">Ticket</div><div>${fmt(s.ticket)}</div></div>
                      <div><div className="text-secondary-auvra">Yield</div><div className="text-auvra-blue">{s.yield_pct}%</div></div>
                      <div><div className="text-secondary-auvra">Term</div><div>{s.term_months}m</div></div>
                      <div><div className="text-secondary-auvra">Payouts</div><div>{paid} / {total}</div></div>
                    </div>
                    <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-auvra-blue" style={{ width: `${total ? (paid / total) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =========================
//  SECONDARY MARKET
// =========================
export function SecondaryMarketPanel() {
  const [listings, setListings] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("browse");
  const [modalSub, setModalSub] = useState(null);
  const [portion, setPortion] = useState("0.25");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [mySales, setMySales] = useState([]);

  const load = () => {
    api.get("/market/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/market/my").then((r) => setMySales(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submitListing = async () => {
    const p = Number(portion), pr = Number(price);
    if (!(p > 0 && p <= 1)) { toast.error("Portion must be 0.01–1.00"); return; }
    if (!(pr > 0)) { toast.error("Price required"); return; }
    setBusy(true);
    try {
      await api.post("/market/list", { subscription_id: modalSub.id, portion: p, price: pr });
      toast.success("Listed on secondary market");
      setModalSub(null); setPortion("0.25"); setPrice(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const buyListing = async (l) => {
    setBusy(true);
    try { await api.post(`/market/buy/${l.id}`); toast.success(`Purchased ${(l.portion * 100).toFixed(0)}% of ${l.offering_title}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const cancel = async (l) => {
    try { await api.post(`/market/cancel/${l.id}`); toast.success("Listing cancelled"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PanelHeader title="Secondary Market" subtitle="List a portion of your subscription tokens for peer resale, or acquire mature positions from other holders at market prices." />
      <div className="flex gap-2 mb-6">
        {[["browse", "Browse"], ["mine", "My Listings"], ["sell", "Sell Portion"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sm-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === k ? "bg-auvra-blue border-auvra-blue text-white" : "border-white/15 text-secondary-auvra hover:border-white/40"}`}>{l}</button>
        ))}
      </div>

      {tab === "browse" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sm-listings">
          {listings.length === 0 && <p className="text-secondary-auvra text-sm">No open listings.</p>}
          {listings.map((l) => (
            <div key={l.id} className="glass rounded-2xl p-6 h-full flex flex-col" data-testid={`sm-listing-${l.id}`}>
              <img src={l.image} alt="" className="w-full h-32 object-cover rounded-xl mb-4" />
              <div className="flex gap-2 flex-wrap mb-2">
                <Chip tone="blue">{(l.portion * 100).toFixed(0)}%</Chip>
                <Chip tone="gray">{l.offering_class}</Chip>
              </div>
              <h4 className="font-semibold">{l.offering_title}</h4>
              <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
                <div><div className="text-secondary-auvra">Yield</div><div className="text-auvra-blue">{l.yield_pct}%</div></div>
                <div><div className="text-secondary-auvra">Term</div><div>{l.term_months}m</div></div>
                <div><div className="text-secondary-auvra">Sched. left</div><div className="font-mono">${fmt(l.remaining_scheduled || 0)}</div></div>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div><div className="text-xs text-secondary-auvra">Ask</div><div className="text-lg font-semibold">${fmt(l.price)}</div></div>
                <Btn onClick={() => buyListing(l)} disabled={busy} data-testid={`sm-buy-${l.id}`}>Buy</Btn>
              </div>
              <div className="text-[10px] text-secondary-auvra mt-3">Seller: {l.seller_name || "—"}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "mine" && (
        <Card testid="sm-my-listings">
          {mySales.length === 0 ? <p className="text-sm text-secondary-auvra">You have no listings yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
                <th className="py-3">Offering</th><th>Portion</th><th>Price</th><th>Status</th><th></th></tr></thead>
              <tbody>{mySales.map((l) => (
                <tr key={l.id} className="border-b border-white/5 last:border-0">
                  <td className="py-3 max-w-[220px] truncate">{l.offering_title}</td>
                  <td>{(l.portion * 100).toFixed(0)}%</td>
                  <td className="font-mono">${fmt(l.price)}</td>
                  <td><Chip tone={l.status === "OPEN" ? "yellow" : l.status === "SOLD" ? "green" : "gray"}>{l.status}</Chip></td>
                  <td>{l.status === "OPEN" && <button onClick={() => cancel(l)} className="text-xs text-red-400 hover:text-red-300">Cancel</button>}</td>
                </tr>))}</tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "sell" && (
        <Card testid="sm-sell">
          <h3 className="font-medium mb-4">Choose a subscription to list</h3>
          {subs.length === 0 ? <p className="text-sm text-secondary-auvra">Subscribe to an offering first in the Marketplace.</p> : (
            <div className="grid md:grid-cols-2 gap-3">
              {subs.filter((s) => s.status === "ACTIVE" && s.ticket > 0).map((s) => (
                <button key={s.id} onClick={() => setModalSub(s)} data-testid={`sm-list-${s.id}`}
                  className="text-left glass rounded-2xl p-5 hover:border-auvra-blue/40 transition-colors">
                  <div className="flex gap-3">
                    <img src={s.image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.offering_title}</div>
                      <div className="text-xs text-secondary-auvra mt-1">Ticket: ${fmt(s.ticket)} · Yield: {s.yield_pct}%</div>
                      <div className="text-xs text-auvra-blue mt-2">Create listing →</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      <Dialog open={!!modalSub} onOpenChange={(o) => !o && setModalSub(null)}>
        <DialogContent className="bg-auvra-elevated border border-white/10 text-white sm:max-w-md" data-testid="sm-list-modal">
          {modalSub && (
            <>
              <DialogHeader><DialogTitle>List a portion of {modalSub.offering_title}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div><div className="text-secondary-auvra">Ticket</div><div>${fmt(modalSub.ticket)}</div></div>
                  <div><div className="text-secondary-auvra">Yield</div><div className="text-auvra-blue">{modalSub.yield_pct}%</div></div>
                  <div><div className="text-secondary-auvra">Term</div><div>{modalSub.term_months}m</div></div>
                </div>
                <Field label="Portion to list (0.01–1.00)">
                  <Input type="number" min="0.01" max="1" step="0.05" value={portion} onChange={(e) => setPortion(e.target.value)} data-testid="sm-portion" />
                </Field>
                <Field label="Ask Price (USD)">
                  <Input type="number" placeholder={String((modalSub.ticket * (Number(portion) || 0.25)).toFixed(0))} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="sm-price" />
                </Field>
                <div className="text-xs text-secondary-auvra bg-white/[0.02] border border-white/5 rounded-xl p-3">
                  Portion of remaining schedule for buyer: <span className="text-white">${fmt((_estimateRemaining(modalSub)) * (Number(portion) || 0))}</span>
                </div>
                <Btn onClick={submitListing} disabled={busy} data-testid="sm-list-submit">{busy ? "Listing…" : "Create Listing"}</Btn>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function _estimateRemaining(sub) {
  return (sub.schedule || []).filter((e) => e.status !== "PAID").reduce((s, e) => s + e.amount, 0);
}

// =========================
//  REFERRALS
// =========================
export function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/referral").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const copyLink = async () => { try { await navigator.clipboard.writeText(data.link); toast.success("Referral link copied"); } catch { toast.error("Copy failed"); } };
  const copyCode = async () => { try { await navigator.clipboard.writeText(data.code); toast.success("Code copied"); } catch { toast.error("Copy failed"); } };
  const share = async () => {
    if (!navigator.share) { copyLink(); return; }
    try { await navigator.share({ title: "AUVRA RWA", text: `Join AUVRA with my referral code ${data.code}`, url: data.link }); } catch { /* cancelled */ }
  };
  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setBusy(true);
    try { const { data: r } = await api.post("/referral/redeem", { code: redeemCode.trim() }); toast.success(`Referred by ${r.referrer_name || "AUVRA member"}`); setRedeemCode(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!data) return <div className="text-sm text-secondary-auvra">Loading…</div>;
  return (
    <div>
      <PanelHeader title="Referrals" subtitle={`Earn ${data.bonus_per_side} AUR for you and your friend on their first deposit.`} />
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Stat label="Your Referrals" value={data.referrals_count} icon={UserIcon} testid="ref-count" />
        <Stat label="Funded (credited)" value={data.credited_count} testid="ref-credited" />
        <Stat label="AUR Credit Earned" value={`${fmt(data.aur_credit)} AUR`} positive={data.aur_credit > 0} testid="ref-credit" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" testid="ref-share-card">
          <h3 className="font-medium mb-4">Share your referral link</h3>
          <Field label="Your Code">
            <div className="flex gap-2">
              <code data-testid="ref-code" className="flex-1 rounded-xl border border-white/10 bg-black px-4 py-3 text-sm font-mono text-auvra-blue">{data.code}</code>
              <button onClick={copyCode} className="border border-white/15 hover:border-auvra-blue rounded-xl px-4 text-sm" data-testid="copy-code"><Copy className="w-4 h-4" /></button>
            </div>
          </Field>
          <div className="mt-4">
            <Field label="Referral Link">
              <div className="flex gap-2">
                <code data-testid="ref-link" className="flex-1 truncate rounded-xl border border-white/10 bg-black px-4 py-3 text-xs font-mono text-auvra-blue">{data.link}</code>
                <button onClick={copyLink} className="border border-white/15 hover:border-auvra-blue rounded-xl px-4 text-sm" data-testid="copy-link"><Copy className="w-4 h-4" /></button>
              </div>
            </Field>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Btn onClick={share} data-testid="ref-share">One-tap Share</Btn>
            <Btn variant="secondary" onClick={copyLink}>Copy Link</Btn>
          </div>
          <div className="mt-6 text-xs text-secondary-auvra bg-white/[0.02] border border-white/5 rounded-xl p-3">
            When your friend signs up with your code and makes their first deposit, you both receive <span className="text-auvra-blue">{data.bonus_per_side} AUR</span> credit automatically.
          </div>
        </Card>
        <Card testid="ref-redeem-card">
          <h3 className="font-medium mb-4">Have a referral code?</h3>
          <Field label="Enter code"><Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="AUVR-XXXXXX" data-testid="redeem-code" /></Field>
          <div className="mt-4"><Btn onClick={redeem} disabled={busy} data-testid="redeem-btn">{busy ? "Applying…" : "Apply Code"}</Btn></div>
          <p className="text-xs text-secondary-auvra mt-4">You can only apply a referral code once. AUR is credited after your first successful deposit.</p>
        </Card>
      </div>
      {data.referrals.length > 0 && (
        <Card className="mt-4" testid="ref-list">
          <h3 className="font-medium mb-4">Your referrals</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-secondary-auvra border-b border-white/5">
              <th className="py-3">Email</th><th>Joined</th><th>Status</th></tr></thead>
            <tbody>{data.referrals.map((r) => (
              <tr key={r.id} className="border-b border-white/5 last:border-0">
                <td className="py-3 truncate max-w-[200px]">{r.referred_email}</td>
                <td className="text-xs text-secondary-auvra">{new Date(r.created_at).toLocaleDateString()}</td>
                <td><Chip tone={r.credited ? "green" : "yellow"}>{r.credited ? "CREDITED" : "PENDING FUNDING"}</Chip></td>
              </tr>))}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// =========================
//  RWA offering detail modal (with AUR credit toggle)
// =========================
export function OfferingModal({ offering, onClose, onSubscribe, aurCredit = 0 }) {
  const [ticket, setTicket] = useState("");
  const [aurApply, setAurApply] = useState("0");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (offering) { setTicket(String(offering.min_ticket)); setAurApply("0"); } }, [offering]);
  const submit = async () => {
    const t = Number(ticket), a = Number(aurApply);
    if (!t || t < (offering?.min_ticket || 0)) { toast.error(`Minimum ticket is $${offering.min_ticket}`); return; }
    setBusy(true);
    try { await onSubscribe(offering, t, a); } finally { setBusy(false); }
  };
  const aurMax = Math.min(aurCredit, Number(ticket) || 0);
  return (
    <Dialog open={!!offering} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-auvra-elevated border border-white/10 text-white sm:max-w-lg" data-testid="offering-modal">
        {offering && (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-3">
              <img src={offering.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
              {offering.title}
            </DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="flex gap-2 flex-wrap"><Chip tone={offering.status === "OPEN" ? "green" : "yellow"}>{offering.status}</Chip><Chip tone="gray">{offering.class}</Chip><Chip tone="blue">{offering.region}</Chip></div>
              <p className="text-sm text-secondary-auvra">{offering.summary}</p>
              <div className="grid grid-cols-3 gap-4">
                <div><div className="text-xs text-secondary-auvra">Target Yield</div><div className="text-auvra-blue font-semibold text-lg">{offering.target_yield}%</div></div>
                <div><div className="text-xs text-secondary-auvra">Term</div><div className="text-lg">{offering.term_months}m</div></div>
                <div><div className="text-xs text-secondary-auvra">Min Ticket</div><div className="text-lg">${fmt(offering.min_ticket, 0)}</div></div>
              </div>
              <Field label="Ticket size (USD)">
                <Input type="number" value={ticket} onChange={(e) => setTicket(e.target.value)} data-testid="off-ticket" />
              </Field>
              {aurCredit > 0 && (
                <div className="glass rounded-xl p-4 border border-auvra-blue/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-secondary-auvra">Apply AUR Credit</span>
                    <span className="text-xs text-auvra-blue">Balance: {fmt(aurCredit)} AUR</span>
                  </div>
                  <div className="flex gap-2">
                    <Input type="number" min="0" max={aurMax} value={aurApply} onChange={(e) => setAurApply(e.target.value)} data-testid="off-aur" />
                    <button onClick={() => setAurApply(String(aurMax))} className="border border-white/15 hover:border-auvra-blue rounded-xl px-3 text-xs">Max</button>
                  </div>
                  <div className="text-[10px] text-secondary-auvra mt-2">1 AUR = $1 · Cash required: <span className="text-white">${fmt(Math.max(0, (Number(ticket) || 0) - (Number(aurApply) || 0)))}</span></div>
                </div>
              )}
              <div>
                <div className="kicker mb-2">Highlights</div>
                <ul className="space-y-1.5 text-sm">
                  {offering.highlights.map((h) => <li key={h} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-auvra-blue mt-0.5 flex-shrink-0" /> {h}</li>)}
                </ul>
              </div>
              <div className="pt-2 border-t border-white/5 text-xs text-secondary-auvra">Eligibility: <span className="text-white">{offering.eligibility}</span></div>
              <Btn onClick={submit} disabled={busy} data-testid="express-interest">{busy ? "Subscribing…" : "Subscribe"}</Btn>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
